import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { DeviceContact } from '../api/deviceContacts';
import { getDeviceContacts, getTerminalInternalConnections } from '../api/deviceContacts';

interface TerminalLevel {
  level_number: number;
  upper_contact: DeviceContact;
  lower_contact: DeviceContact;
}

interface SingleTerminalInstanceNodeProps {
  data: {
    assignment: any;
    position: number;
  };
}

const SingleTerminalInstanceNode: React.FC<SingleTerminalInstanceNodeProps> = ({ data }) => {
  const { assignment, position } = data;
  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  const [terminalLevels, setTerminalLevels] = useState<TerminalLevel[]>([]);
  const [loading, setLoading] = useState(true);

  const specificationId = assignment.specification_id;

  useEffect(() => {
    if (!specificationId) {
      console.warn('⚠️ No specification_id found for terminal assignment');
      setLoading(false);
      return;
    }
    
    const loadTerminalStructure = async () => {
      try {
        setLoading(true);
        
        // Load contacts and internal connections in parallel
        const [contactsData, connectionsData] = await Promise.all([
          getDeviceContacts(specificationId),
          getTerminalInternalConnections(specificationId)
        ]);
        
        setContacts(contactsData);
        
        // Create ordered contact list: 1U, 2U, 3U, 3D, 2D, 1D
        if (connectionsData.length > 0) {
          // Extract all U and D contacts from connections
          const allLevelContacts: DeviceContact[] = [];
          
          // Get unique level numbers and sort them (filter out null/0)
          const levels = [...new Set(connectionsData.map(conn => conn.level_number).filter((ln): ln is number => ln !== null && ln !== undefined && ln > 0))].sort((a, b) => a - b);
          
          // First pass: add all U contacts in ascending order (1U, 2U, 3U)
          levels.forEach(levelNum => {
            const levelConnections = connectionsData.filter(conn => conn.level_number === levelNum);
            levelConnections.forEach(conn => {
              const upperContact = contactsData.find(c => c.id === conn.contact_from_id);
              if (upperContact) {
                allLevelContacts.push(upperContact);
              }
            });
          });
          
          // Second pass: add all D contacts in descending order (3D, 2D, 1D)
          levels.reverse().forEach(levelNum => {
            const levelConnections = connectionsData.filter(conn => conn.level_number === levelNum);
            levelConnections.forEach(conn => {
              const lowerContact = contactsData.find(c => c.id === conn.contact_to_id);
              if (lowerContact) {
                allLevelContacts.push(lowerContact);
              }
            });
          });
          
          setTerminalLevels(allLevelContacts.map(contact => ({
            level_number: 0, // Not used anymore 
            upper_contact: contact,
            lower_contact: contact // Same contact for simplified structure
          })));
        } else {
          // Fallback for terminals without internal connections (single-level terminals)
          // Show contacts directly from contactsData
          const allContacts: DeviceContact[] = [];
          
          // Get U contacts in ascending order (1U, 2U, 3U)
          const uContacts = contactsData
            .filter(c => typeof c.contact_label === 'string' && c.contact_label.endsWith('U'))
            .sort((a, b) => {
              const numA = parseInt(a.contact_label?.replace('U', '') || '0');
              const numB = parseInt(b.contact_label?.replace('U', '') || '0');
              return numA - numB;
            });
          allContacts.push(...uContacts);
          
          // Get D contacts in descending order (3D, 2D, 1D)
          const dContacts = contactsData
            .filter(c => typeof c.contact_label === 'string' && c.contact_label.endsWith('D'))
            .sort((a, b) => {
              const numA = parseInt(a.contact_label?.replace('D', '') || '0');
              const numB = parseInt(b.contact_label?.replace('D', '') || '0');
              return numB - numA;
            });
          allContacts.push(...dContacts);
          
          setTerminalLevels(allContacts.map(contact => ({
            level_number: 0,
            upper_contact: contact,
            lower_contact: contact
          })));
        }
        
      } catch (error) {
        console.error(`❌ Error loading terminal structure for specification ${specificationId}:`, error);
        setTerminalLevels([]);
      } finally {
        setLoading(false);
      }
    };

    loadTerminalStructure();
  }, [specificationId]);

  if (loading) {
    return (
      <div className="terminal-instance-container bg-white border-2 border-gray-300 rounded-lg p-2 shadow-lg" style={{ width: '35px', minHeight: '120px' }}>
        <div className="text-xs text-gray-500 text-center">...</div>
      </div>
    );
  }

  return (
    <div className="terminal-instance-container bg-white border-2 border-blue-500 rounded-lg p-2 shadow-lg" style={{ width: '35px', minHeight: '120px' }}>
      {/* Terminal Instance Header */}
      <div className="terminal-instance-header text-center mb-2 border-b border-blue-200 pb-1">
        <div className="terminal-instance-position font-bold text-xs text-blue-800">
          {position}
        </div>
      </div>
      
      {/* Terminal Levels Column */}
      <div className="terminal-levels-column flex flex-col items-center space-y-7">
        {terminalLevels.map((level) => (
          <div key={`contact-${level.upper_contact.id}`} className="relative terminal-contact-circle w-6 h-6 rounded-full bg-gray-300 border-2 border-gray-600 cursor-pointer flex items-center justify-center text-xs font-bold text-gray-800">
            {level.upper_contact.contact_label}
            
            {/* Add handle only for D contacts (contacts ending with 'D') */}
            {level.upper_contact.contact_label && 
             typeof level.upper_contact.contact_label === 'string' && 
             level.upper_contact.contact_label.endsWith('D') && (
              <Handle
                type="source"
                position={Position.Left}
                id={`${level.upper_contact.id}-source`}
                className="schema-source-handle w-3 h-3 bg-green-500 border border-green-700"
                style={{ 
                  position: 'absolute', 
                  left: '0px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  borderRadius: '50%',
                  zIndex: 1000,
                  pointerEvents: 'all'
                }}
              />
            )}
          </div>
        ))}
        
        {/* Fallback: if no levels found, show contacts in old format */}
        {terminalLevels.length === 0 && contacts.length > 0 && (
          <>
            {/* Upper contacts without handles */}
            {contacts.filter(contact => 
              typeof contact.contact_label === 'string' && contact.contact_label.endsWith('U')
            ).reverse().map((contact) => (
              <div key={contact.id} className="terminal-contact-circle w-6 h-6 rounded-full bg-gray-300 border-2 border-gray-600 cursor-pointer flex items-center justify-center text-xs font-bold text-gray-800">
                {contact.contact_label}
              </div>
            ))}
            
            {/* Spacer between upper and lower contacts */}
            <div className="h-2"></div>
            
            {/* Lower contacts with connection handles */}
            {contacts.filter(contact => 
              typeof contact.contact_label === 'string' && contact.contact_label.endsWith('D')
            ).reverse().map((contact) => (
              <div key={contact.id} className="relative terminal-contact-circle w-6 h-6 rounded-full bg-gray-300 border-2 border-gray-600 cursor-pointer flex items-center justify-center text-xs font-bold text-gray-800">
                {contact.contact_label}
                
                <Handle
                  type="source"
                  position={Position.Left}
                  id={`${contact.id}-source`}
                  className="schema-source-handle w-3 h-3 bg-green-500 border border-green-700"
                  style={{ 
                    position: 'absolute', 
                    left: '0px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    borderRadius: '50%',
                    zIndex: 1000,
                    pointerEvents: 'all'
                  }}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default SingleTerminalInstanceNode; 